import React, { useEffect, useRef, useContext } from "react";
import * as echarts from "echarts";
import { useTranslation } from "react-i18next";
import { ThemeContext } from "../../..";

const CommunityKitchenTrendView = (props) => {
  const echartsDom = useRef(null);
  const { theme } = useContext(ThemeContext);
  const { t } = useTranslation();

  const kidsTotal = Object.entries(props.dataval || {}).reduce((acc, [key, value]) => {
    if (key.includes("Children Male") || key.includes("Children Female")) return acc + value;
    return acc;
  }, 0);

  const menTotal = Object.entries(props.dataval || {}).reduce((acc, [key, value]) => {
    if (key.includes("Males") && !key.includes("Females") && !key.includes("Transgender")) return acc + value;
    if (key.includes("Males Disabled")) return acc + value;
    return acc;
  }, 0);

  const womenTotal = Object.entries(props.dataval || {}).reduce((acc, [key, value]) => {
    if (key.includes("Females") && !key.includes("Transgender") && !key.includes("Males")) return acc + value;
    if (key.includes("Females Disabled") || key.includes("Females Postnatal") || key.includes("Females Pregnant")) return acc + value;
    return acc;
  }, 0);

  const transgenderTotal = Object.entries(props.dataval || {}).reduce((acc, [key, value]) => {
    if (key.includes("Transgender") && !key.includes("Females") && !key.includes("Males")) return acc + value;
    if (key.includes("Transgender Disabled")) return acc + value;
    return acc;
  }, 0);

  const total = menTotal + womenTotal + kidsTotal + transgenderTotal;

  const palette = {
    males:       theme === "light" ? "#0b2d4d" : "#59b3ff",
    females:     theme === "light" ? "#1a4a73" : "#93cfff",
    children:    theme === "light" ? "#59b3ff" : "#0b2d4d",
    transgender: theme === "light" ? "#b0c8e0" : "#2d6da3",
    text:        theme === "light" ? "#0b2d4d" : "#ffffff",
    subtext:     theme === "light" ? "#64748b" : "rgba(255,255,255,0.5)",
  };

  const seriesData = [
    { value: menTotal,         name: "Males",       itemStyle: { color: palette.males } },
    { value: womenTotal,       name: "Females",     itemStyle: { color: palette.females } },
    { value: kidsTotal,        name: "Children",    itemStyle: { color: palette.children } },
    { value: transgenderTotal, name: "Transgender", itemStyle: { color: palette.transgender } },
  ];

  const options = {
    backgroundColor: "transparent",
    tooltip: {
      trigger: "item",
      confine: true,
      backgroundColor: "rgba(11,45,77,0.92)",
      borderColor: "transparent",
      textStyle: { color: "#fff", fontSize: 12 },
      borderRadius: 8,
      formatter: (p) => {
        const pct = total > 0 ? ((p.value / total) * 100).toFixed(1) : 0;
        return `<b>${p.name}</b><br/>${p.value.toLocaleString()} &nbsp;(${pct}%)`;
      },
    },
    legend: { show: false },
    series: [
      {
        name: "Members",
        type: "pie",
        radius: ["48%", "72%"],
        center: ["50%", "50%"],
        avoidLabelOverlap: false,
        label: { show: false },
        labelLine: { show: false },
        itemStyle: { borderWidth: 2, borderColor: theme === "light" ? "#ffffff" : "#1e293b" },
        emphasis: {
          scale: true,
          scaleSize: 4,
          itemStyle: { shadowBlur: 10, shadowColor: "rgba(11,45,77,0.25)" },
          label: { show: false },
        },
        data: seriesData,
      },
    ],
    graphic: [
      {
        type: "text",
        left: "center",
        top: "40%",
        style: {
          text: total > 0 ? total.toLocaleString() : "0",
          fill: palette.text,
          fontSize: 20,
          fontWeight: "bold",
          textAlign: "center",
        },
      },
      {
        type: "text",
        left: "center",
        top: "52%",
        style: {
          text: "Total",
          fill: palette.subtext,
          fontSize: 10,
          textAlign: "center",
        },
      },
    ],
  };

  useEffect(() => {
    if (!echartsDom.current) return;
    const myChart = echarts.init(echartsDom.current);
    myChart.setOption(options);
    const handleResize = () => myChart.resize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      myChart.dispose();
    };
  }, [props.dataval, theme]);

  const legendItems = [
    { label: "Males",       value: menTotal,         color: palette.males },
    { label: "Females",     value: womenTotal,       color: palette.females },
    { label: "Children",    value: kidsTotal,        color: palette.children },
    { label: "Transgender", value: transgenderTotal, color: palette.transgender },
  ];

  return (
    <div className="card" style={{ width: "100%", height: "95%" }}>
      <div className="card-body" style={{ height: "100%", display: "flex", flexDirection: "column", padding: "0.8rem 1rem" }}>
        <div className="card-title" style={{ color: "#0b2d4d", marginBottom: "0.5rem" }}>
          <span className="card-title-icon">
            <svg width="11" height="11" viewBox="0 0 24 24" fill="white">
              <path d="M8.1 13.34l2.83-2.83L3.91 3.5a4.008 4.008 0 000 5.66l4.19 4.18zm6.78-1.81c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.46-4.2-1.1-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L3.7 19.87l1.41 1.41L12 14.41l6.88 6.88 1.41-1.41L13.41 13l1.47-1.47z"/>
            </svg>
          </span>
          {t("navlinklabels.communitykitchen")}
        </div>

        {/* Donut chart */}
        <div ref={echartsDom} style={{ flex: 1, minHeight: 0 }} />

        {/* Legend below chart — 2x2 grid with dot + label + value */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "4px 16px",
          paddingTop: "6px",
          borderTop: `1px solid ${theme === "light" ? "#e8f0fe" : "rgba(255,255,255,0.08)"}`,
        }}>
          {legendItems.map((item) => (
            <div key={item.label} title={item.label} style={{
              display: "flex",
              alignItems: "center",
              gap: "5px",
            }}>
              <span style={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                background: item.color,
                flexShrink: 0,
              }} />
              <span style={{
                fontSize: "0.58rem",
                fontWeight: 500,
                color: theme === "light" ? "#334155" : "#cbd5e1",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}>
                {item.label}
              </span>
              <span style={{
                fontSize: "0.65rem",
                fontWeight: 700,
                color: theme === "light" ? "#0b2d4d" : "#fff",
                marginLeft: "auto",
              }}>
                {item.value.toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CommunityKitchenTrendView;